export enum GridifyConditionEnum {
  Equal = "=",
  NotEqual = "!=",
  LessThan = "<",
  GreaterThan = ">",
  GreaterThanOrEqual = ">=",
  LessThanOrEqual = "<=",
  Contains = "=*",
  NotContains = "!*",
  StartsWith = "^",
  NotStartsWith = "!^",
  EndsWith = "$",
  NotEndsWith = "!$",
}

export enum GridifyLogicalEnum {
  And = ",",
  Or = "|",
  CaseInsensitive = "/i",
}

export enum GridifyOrderEnum {
  Ascending = " asc",
  Descending = " desc",
}

export interface OptionFields {
  /** @default false */
  caseInsensitive?: boolean;
  order?: GridifyOrderEnum;
  /** @default GridifyLogicalEnum.And for multiple entries & GridifyLogicalEnum.Or for multiple values */
  logic?: GridifyLogicalEnum;
  parenthesis?: boolean;
}

interface OptionsType {
  options: OptionFields;
}

export type FilterType<FormType> = {
  [key in keyof Partial<FormType>]:
    | [GridifyConditionEnum, FormType[key] | FormType[key][]]
    | [GridifyConditionEnum, FormType[key] | FormType[key][], OptionFields];
};

type OperatorType = GridifyLogicalEnum.Or | GridifyLogicalEnum.And;
export type FiltersType<FormType = Record<string, any>> = (
  | OperatorType
  | OptionsType
  | FilterType<FormType>
  | FiltersType
)[];

function trimEnds(query: string): string {
  let result = query;
  // There should not be any starting or ending operator
  if (result.startsWith(GridifyLogicalEnum.And)) result = result.slice(GridifyLogicalEnum.And.length);
  if (result.startsWith(GridifyLogicalEnum.Or)) result = result.slice(GridifyLogicalEnum.Or.length);
  if (result.endsWith(GridifyLogicalEnum.And)) result = result.slice(0, -GridifyLogicalEnum.And.length);
  if (result.endsWith(GridifyLogicalEnum.Or)) result = result.slice(0, -GridifyLogicalEnum.Or.length);
  return result;
}

/** if our operator starts with Not*, our default logic should be `And`. */
function getDefaultLogic(operator: GridifyConditionEnum): GridifyLogicalEnum {
  return [
    GridifyConditionEnum.NotContains,
    GridifyConditionEnum.NotEqual,
    GridifyConditionEnum.NotEndsWith,
    GridifyConditionEnum.NotStartsWith,
  ].includes(operator)
    ? GridifyLogicalEnum.And
    : GridifyLogicalEnum.Or;
}

export function createQuery<FormType>(filters: FiltersType<FormType>) {
  const queries: string = filters.reduce((prevQuery, filter) => {
    // If current filter is an `string`, it means it is only an operator between two other filters
    if (typeof filter === "string") return prevQuery + filter;

    // If current filter is an array inside the current array then it should be wrapped in Parentheses
    if (Array.isArray(filter)) {
      const childQueries = createQuery(filter);
      return childQueries ? trimEnds(`${prevQuery}(${childQueries})`) : trimEnds(prevQuery);
    }

    // Multiple entry in one object
    const { options } = filter as OptionsType;
    const otherFilters = filter as FilterType<FormType>;
    const entryLogic = options?.logic || GridifyLogicalEnum.And;
    const entryOrder = options?.order || "";
    const entryCaseInsensitive = options?.caseInsensitive || "";

    const res = Object.entries<[GridifyConditionEnum, any] | [GridifyConditionEnum, any, OptionFields]>(
      otherFilters
    ).reduce((accFilter, [key, array]) => {
      // Options entry is calculated before entring this loop
      if (key === "options") return accFilter;

      const [operator, allValue, optionsField] = array;

      // Single entry for one key
      if (key && allValue) {
        const entry = (Array.isArray(allValue) ? allValue : [allValue])
          .map((value) => key + operator + value)
          .join(optionsField?.logic || options?.logic || getDefaultLogic(operator));
        return trimEnds(
          accFilter +
            entryOrder +
            entryCaseInsensitive +
            entryLogic +
            (optionsField?.parenthesis ? `(${entry})` : entry) +
            (optionsField?.order || entryOrder) +
            (optionsField?.caseInsensitive || entryCaseInsensitive ? GridifyLogicalEnum.CaseInsensitive : "")
        );
      }

      if (key && options?.order) return trimEnds(`${accFilter + key} ${options.order}`);

      return trimEnds(accFilter);
    }, "");

    return trimEnds(prevQuery + res);
  }, "");
  return queries;
}
